import { useEffect, useMemo, useCallback, useState } from 'react'
import { useScreener } from '@/hooks/useScreener'
import { useNavigate } from 'react-router-dom'
import { navigateToTerminalCommand } from '@/lib/navigation'
import {
  Filter, Loader2, RefreshCw, Copy, Bot, ChevronUp, ChevronDown,
  Zap, TrendingUp, TrendingDown, Users, BarChart2, Target, Star,
  Monitor, CandlestickChart,
} from 'lucide-react'
import { clsx } from 'clsx'
import TickerLink from '@/components/ui/TickerLink'
import { getTopRated } from '@/lib/api'
import type { ScreenerStock, StockRating } from '@/lib/types'

// ── Helpers ──────────────────────────────────────────────────────────────────

function pct(n: number | null | undefined) { const v = (n == null || !Number.isFinite(n)) ? 0 : n; return `${v >= 0 ? '+' : ''}${v.toFixed(2)}%` }
function usd(n: number | null | undefined) { const v = (n == null || !Number.isFinite(n)) ? 0 : n; return `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` }
function capFmt(m: number): string {
  if (m >= 1_000_000) return `$${(m / 1_000_000).toFixed(1)}T`
  if (m >= 1_000) return `$${(m / 1_000).toFixed(0)}B`
  return `$${m.toFixed(0)}M`
}

const PRESET_ICONS: Record<string, typeof Zap> = {
  oversold_insider: TrendingDown,
  high_short_rising: TrendingUp,
  congress_favorites: Users,
  momentum_breakout: Zap,
  value_plays: Target,
  top_quant_rated: Star,
  dividend_income: BarChart2,
  quality_value: Target,
  my_holdings: Star,
}

// ── Grade color mapping ────────────────────────────────────────────────────

function gradeColor(grade: string): string {
  switch (grade) {
    case 'A+': case 'A': return 'bg-emerald/15 text-emerald-light'
    case 'A-': case 'B+': return 'bg-emerald/10 text-emerald'
    case 'B': return 'bg-sky-500/20 text-sky-400'
    case 'B-': case 'C+': return 'bg-amber/15 text-amber-light'
    case 'C': case 'D': return 'bg-amber/20 text-amber'
    case 'F': return 'bg-rose/15 text-rose-light'
    default: return 'bg-surface-hover text-text-muted'
  }
}

function ratingColor(rating: number): string {
  if (rating >= 4.0) return 'bg-emerald/15 text-emerald-light border-emerald/30'
  if (rating >= 3.0) return 'bg-sky-500/20 text-sky-400 border-sky-500/30'
  if (rating >= 2.0) return 'bg-amber/15 text-amber-light border-amber/30'
  return 'bg-rose/15 text-rose-light border-rose/30'
}

// ── Grade Badge Component ──────────────────────────────────────────────────

function GradeBadge({ grade }: { grade: string | undefined }) {
  if (!grade) return <span className="text-text-muted text-xs">-</span>
  return (
    <span className={clsx(
      'text-[10px] font-bold px-1.5 py-0.5 rounded',
      gradeColor(grade),
    )}>
      {grade}
    </span>
  )
}

function QuantBadge({ rating }: { rating: number | undefined }) {
  if (rating == null) return <span className="text-text-muted text-xs">-</span>
  return (
    <span className={clsx(
      'text-[10px] font-bold px-2 py-0.5 rounded border',
      ratingColor(rating),
    )}>
      {(rating ?? 0).toFixed(2)}
    </span>
  )
}

// ── Range Slider Component ──────────────────────────────────────────────────

function RangeFilter({ label, min, max, step, valueMin, valueMax, onChange }: {
  label: string
  min: number
  max: number
  step: number
  valueMin: number | undefined
  valueMax: number | undefined
  onChange: (vMin: number | undefined, vMax: number | undefined) => void
}) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-medium text-text-secondary">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="number"
          step={step}
          min={min}
          max={max}
          value={valueMin ?? ''}
          placeholder="Min"
          onChange={e => onChange(e.target.value ? Number(e.target.value) : undefined, valueMax)}
          className="input w-20 px-2 py-1 text-xs rounded"
        />
        <span className="text-text-muted text-xs">-</span>
        <input
          type="number"
          step={step}
          min={min}
          max={max}
          value={valueMax ?? ''}
          placeholder="Max"
          onChange={e => onChange(valueMin, e.target.value ? Number(e.target.value) : undefined)}
          className="input w-20 px-2 py-1 text-xs rounded"
        />
      </div>
    </div>
  )
}

// ── Select Component ────────────────────────────────────────────────────────

function SelectFilter({ label, value, options, onChange }: {
  label: string
  value: string | undefined
  options: { value: string; label: string }[]
  onChange: (v: string | undefined) => void
}) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-medium text-text-secondary">{label}</label>
      <select
        value={value ?? ''}
        onChange={e => onChange(e.target.value || undefined)}
        className="input w-full px-2 py-1.5 text-xs rounded"
        aria-label={label}
      >
        <option value="">Any</option>
        {options.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  )
}

// ── Sort Header ─────────────────────────────────────────────────────────────

function SortHeader({ label, field, sortField, sortDir, onSort }: {
  label: string
  field: string
  sortField: string
  sortDir: 'asc' | 'desc'
  onSort: (f: string) => void
}) {
  const active = field === sortField
  return (
    <button
      onClick={() => onSort(field)}
      className={clsx(
        'flex items-center gap-1 text-xs font-medium whitespace-nowrap',
        active ? 'text-blue-light' : 'text-text-secondary hover:text-text-primary',
      )}
    >
      {label}
      {active && (sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
    </button>
  )
}

// ── Signal Badge ────────────────────────────────────────────────────────────

function SignalBadge({ signal }: { signal: string }) {
  if (signal === 'None') return <span className="text-text-muted text-xs">-</span>
  return (
    <span className={clsx(
      'text-[10px] font-semibold px-1.5 py-0.5 rounded',
      signal === 'Buying' ? 'bg-emerald/15 text-emerald-light' : 'bg-rose/15 text-rose-light',
    )}>
      {signal}
    </span>
  )
}

// ── Main Page ───────────────────────────────────────────────────────────────

export default function ScreenerPage() {
  const {
    results, count, heldCount, sectors, presets, filters, loading, error,
    sortField, sortDir, asOf, running, hasRun, note,
    setFilters, resetFilters, applyPreset, setSortField, run, fetchStored,
    fetchSectors, fetchPresets,
  } = useScreener()
  const navigate = useNavigate()

  // Quant ratings data keyed by ticker
  const [ratings, setRatings] = useState<Record<string, StockRating>>({})
  const [ratingsLoading, setRatingsLoading] = useState(false)

  useEffect(() => {
    fetchSectors()
    fetchPresets()
    // Load the LAST STORED result instantly — a fresh scan only runs when
    // the user presses Run (the backend computes it in the background).
    fetchStored()
    // Load quant ratings for the universe
    loadRatings()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const loadRatings = useCallback(async () => {
    setRatingsLoading(true)
    try {
      const res = await getTopRated(50)
      const map: Record<string, StockRating> = {}
      for (const r of res.ratings) {
        map[r.ticker] = r
      }
      setRatings(map)
    } catch {
      // Ratings are supplementary, don't block the screener
    } finally {
      setRatingsLoading(false)
    }
  }, [])

  // Sort results client-side
  const sorted = useMemo(() => {
    const arr = [...results]
    arr.sort((a, b) => {
      // Handle quant_rating sort via ratings map
      if (sortField === 'quant_rating') {
        const ar = ratings[a.ticker]?.quant_rating ?? 0
        const br = ratings[b.ticker]?.quant_rating ?? 0
        return sortDir === 'asc' ? ar - br : br - ar
      }
      const av = (a as unknown as Record<string, unknown>)[sortField]
      const bv = (b as unknown as Record<string, unknown>)[sortField]
      const an = typeof av === 'number' ? av : 0
      const bn = typeof bv === 'number' ? bv : 0
      return sortDir === 'asc' ? an - bn : bn - an
    })
    return arr
  }, [results, sortField, sortDir, ratings])

  // Export CSV
  const exportCSV = useCallback(() => {
    const headers = ['Ticker', 'Name', 'Held', 'Shares', 'AvgCost', 'UnrealisedPnL', 'Price', 'Change%', 'Volume(M)', 'MktCap(M)', 'PE', 'PB', 'PS', 'PEG', 'DivYield%', 'Beta', 'RSI', 'Short%', 'Quant', 'V', 'G', 'P', 'M', 'E', 'Insider', 'Congress']
    const rows = sorted.map(s => {
      const r = ratings[s.ticker]
      return [
        s.ticker, s.name,
        s.held ? 'Y' : '', s.position?.shares ?? '', s.position?.avg_cost ?? '', s.position?.unrealised_pnl ?? '',
        s.price, s.daily_change_pct, s.volume_m,
        s.market_cap_m, s.pe_ratio ?? '', s.pb_ratio ?? '', s.ps_ratio ?? '', s.peg_ratio ?? '',
        s.dividend_yield ?? '', s.beta ?? '', s.rsi ?? '', s.short_pct ?? '',
        r?.quant_rating?.toFixed(2) ?? '', r?.valuation.grade ?? '', r?.growth.grade ?? '',
        r?.profitability.grade ?? '', r?.momentum.grade ?? '', r?.eps_revisions.grade ?? '',
        s.insider_signal, s.congress_signal,
      ].join(',')
    })
    const csv = [headers.join(','), ...rows].join('\n')
    navigator.clipboard.writeText(csv)
  }, [sorted, ratings])

  // Send to agents
  const sendToAgents = useCallback(() => {
    const tickers = sorted.map(s => s.ticker).join(',')
    navigate(`/app/agents?tickers=${tickers}`)
  }, [sorted, navigate])

  // Presets come from the backend (which now includes top_quant_rated,
  // dividend_income, quality_value, my_holdings). Dedup defensively in case an
  // older cached backend response still lacks them.
  const allPresets = useMemo(() => {
    const seen = new Set<string>()
    const out = [...presets]
    for (const p of out) seen.add(p.id)
    if (!seen.has('top_quant_rated')) {
      out.push({ id: 'top_quant_rated', name: 'Top Quant Rated', description: 'Stocks with quant rating > 4.0' })
    }
    return out
  }, [presets])

  return (
    <div className="flex h-full">
      {/* ── Filter Panel (left) ──────────────────────────────────────────── */}
      <aside className="w-64 shrink-0 border-r border-border overflow-y-auto">
        <div className="px-4 py-4 border-b border-border">
          <div className="flex items-center gap-2 mb-3">
            <Filter size={16} className="text-purple-light" />
            <h2 className="text-sm font-bold text-text-primary">Filters</h2>
          </div>

          {/* Presets */}
          <div className="space-y-1 mb-4">
            <span className="panel-title">Quick Presets</span>
            {allPresets.map(p => {
              const Icon = PRESET_ICONS[p.id] ?? BarChart2
              return (
                <button
                  key={p.id}
                  onClick={() => applyPreset(p.id)}
                  className={clsx(
                    'w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs text-left transition-colors',
                    filters.preset === p.id
                      ? 'bg-purple/15 text-purple-light'
                      : 'text-text-secondary hover:bg-surface-card hover:text-text-primary',
                  )}
                  title={p.description}
                >
                  <Icon size={12} />
                  {p.name}
                </button>
              )
            })}
          </div>
        </div>

        <div className="px-4 py-3 space-y-3">
          <RangeFilter
            label="Market Cap ($M)"
            min={0} max={4_000_000} step={10_000}
            valueMin={filters.market_cap_min} valueMax={filters.market_cap_max}
            onChange={(mn, mx) => setFilters({ market_cap_min: mn, market_cap_max: mx })}
          />

          <RangeFilter
            label="P/E Ratio"
            min={0} max={200} step={1}
            valueMin={filters.pe_min} valueMax={filters.pe_max}
            onChange={(mn, mx) => setFilters({ pe_min: mn, pe_max: mx })}
          />

          <RangeFilter
            label="Daily Change %"
            min={-20} max={20} step={0.5}
            valueMin={filters.change_min} valueMax={filters.change_max}
            onChange={(mn, mx) => setFilters({ change_min: mn, change_max: mx })}
          />

          <RangeFilter
            label="Volume (M)"
            min={0} max={500} step={5}
            valueMin={filters.volume_min} valueMax={undefined}
            onChange={(mn) => setFilters({ volume_min: mn })}
          />

          <SelectFilter
            label="Sector"
            value={filters.sector}
            options={sectors.map(s => ({ value: s, label: s }))}
            onChange={v => setFilters({ sector: v })}
          />

          <RangeFilter
            label="RSI"
            min={0} max={100} step={1}
            valueMin={filters.rsi_min} valueMax={filters.rsi_max}
            onChange={(mn, mx) => setFilters({ rsi_min: mn, rsi_max: mx })}
          />

          <RangeFilter
            label="Short Interest %"
            min={0} max={100} step={1}
            valueMin={filters.short_pct_min} valueMax={filters.short_pct_max}
            onChange={(mn, mx) => setFilters({ short_pct_min: mn, short_pct_max: mx })}
          />

          <SelectFilter
            label="Insider Activity"
            value={filters.insider_activity}
            options={[
              { value: 'buying', label: 'Buying' },
              { value: 'selling', label: 'Selling' },
              { value: 'any', label: 'Any Activity' },
            ]}
            onChange={v => setFilters({ insider_activity: v })}
          />

          <SelectFilter
            label="Congress Activity"
            value={filters.congress_activity}
            options={[
              { value: 'buying', label: 'Buying' },
              { value: 'selling', label: 'Selling' },
              { value: 'any', label: 'Any Activity' },
            ]}
            onChange={v => setFilters({ congress_activity: v })}
          />

          {/* ── Portfolio ── */}
          <div className="pt-1 border-t border-border/60">
            <span className="panel-title">Portfolio</span>
          </div>
          <SelectFilter
            label="Holdings"
            value={filters.held}
            options={[
              { value: 'only', label: 'Only stocks I hold' },
              { value: 'exclude', label: 'Exclude what I hold' },
            ]}
            onChange={v => setFilters({ held: v as 'only' | 'exclude' | undefined })}
          />

          {/* ── Fundamentals ── */}
          <div className="pt-1 border-t border-border/60">
            <span className="panel-title">Fundamentals</span>
          </div>
          <RangeFilter
            label="Price / Book"
            min={0} max={50} step={0.5}
            valueMin={filters.pb_min} valueMax={filters.pb_max}
            onChange={(mn, mx) => setFilters({ pb_min: mn, pb_max: mx })}
          />
          <RangeFilter
            label="Price / Sales"
            min={0} max={50} step={0.5}
            valueMin={filters.ps_min} valueMax={filters.ps_max}
            onChange={(mn, mx) => setFilters({ ps_min: mn, ps_max: mx })}
          />
          <RangeFilter
            label="PEG (max)"
            min={0} max={10} step={0.1}
            valueMin={undefined} valueMax={filters.peg_max}
            onChange={(_mn, mx) => setFilters({ peg_max: mx })}
          />
          <RangeFilter
            label="Dividend Yield %"
            min={0} max={15} step={0.1}
            valueMin={filters.div_yield_min} valueMax={filters.div_yield_max}
            onChange={(mn, mx) => setFilters({ div_yield_min: mn, div_yield_max: mx })}
          />
          <RangeFilter
            label="Beta"
            min={0} max={5} step={0.1}
            valueMin={filters.beta_min} valueMax={filters.beta_max}
            onChange={(mn, mx) => setFilters({ beta_min: mn, beta_max: mx })}
          />
          <RangeFilter
            label="Quant Rating (min)"
            min={0} max={5} step={0.1}
            valueMin={filters.quant_rating_min} valueMax={undefined}
            onChange={(mn) => setFilters({ quant_rating_min: mn })}
          />

          <div className="flex gap-2 pt-2">
            <button
              onClick={() => run()}
              disabled={loading}
              className="btn-primary flex-1 py-2 text-xs font-semibold"
            >
              {loading ? 'Scanning...' : 'Run Screener'}
            </button>
            <button
              onClick={resetFilters}
              className="btn-secondary px-3 py-2 text-xs"
            >
              Reset
            </button>
          </div>
        </div>
      </aside>

      {/* ── Results Panel (right) ────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header bar */}
        <div className="px-4 py-3 border-b border-border flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-purple/15 flex items-center justify-center">
              <Filter size={16} className="text-purple-light" />
            </div>
            <div>
              <h1 className="page-title text-lg md:text-xl">Stock Screener</h1>
              <p className="text-[10px] text-text-muted">
                {count} {count === 1 ? 'result' : 'results'} found
                {heldCount > 0 && <span className="text-cyan-light"> · {heldCount} held</span>}
                {asOf && (
                  <span> · as of {new Date(asOf).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span>
                )}
                {(running || loading) && <span className="text-amber-light"> · scanning…</span>}
                {ratingsLoading && ' | Loading ratings...'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={exportCSV}
              className="btn-secondary px-3 py-1.5 text-xs gap-1.5"
              title="Copy results as CSV"
            >
              <Copy size={12} />
              Export CSV
            </button>
            <button
              onClick={sendToAgents}
              disabled={sorted.length === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium
                         bg-purple/15 text-purple-light hover:bg-purple/25
                         disabled:opacity-50 transition-colors"
            >
              <Bot size={12} />
              Send to Agents
            </button>
            <button
              type="button"
              onClick={() => run()}
              className="p-2 rounded-lg hover:bg-surface-card text-text-muted hover:text-text-primary transition-colors"
              aria-label="Refresh screener"
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto">
          {loading && results.length === 0 && (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-6 h-6 animate-spin text-blue" />
              <span className="ml-2 text-text-muted text-sm">Scanning in the background — results will land here...</span>
            </div>
          )}

          {error && (
            <div className="flex items-center justify-center h-64">
              <p className="text-rose-light text-sm">{error}</p>
            </div>
          )}

          {!loading && results.length === 0 && !error && !hasRun && (
            <div className="flex flex-col items-center justify-center h-64 gap-3 px-4 text-center">
              <p className="text-text-muted text-sm">{note ?? 'Screener has not been run yet — press Run.'}</p>
              <button
                type="button"
                onClick={() => run()}
                className="btn-primary px-4 py-2 text-xs font-semibold"
              >
                Run Screener
              </button>
            </div>
          )}

          {!loading && results.length === 0 && !error && hasRun && (
            <div className="flex items-center justify-center h-64">
              <p className="text-text-muted text-sm">No stocks match your filters. Try adjusting criteria.</p>
            </div>
          )}

          {sorted.length > 0 && (
            <div className="table-responsive">
            <table className="w-full text-xs table-pro">
              <thead>
                <tr>
                  {[
                    { label: 'Ticker', field: 'ticker' },
                    { label: 'Name', field: 'name' },
                    { label: 'Price', field: 'price' },
                    { label: 'Change', field: 'daily_change_pct' },
                    { label: 'Quant', field: 'quant_rating' },
                    { label: 'V', field: '_val' },
                    { label: 'G', field: '_gro' },
                    { label: 'P', field: '_pro' },
                    { label: 'M', field: '_mom' },
                    { label: 'E', field: '_eps' },
                    { label: 'Mkt Cap', field: 'market_cap_m' },
                    { label: 'P/E', field: 'pe_ratio' },
                    { label: 'P/B', field: 'pb_ratio' },
                    { label: 'P/S', field: 'ps_ratio' },
                    { label: 'Div%', field: 'dividend_yield' },
                    { label: 'Beta', field: 'beta' },
                    { label: 'RSI', field: 'rsi' },
                    { label: 'Insider', field: 'insider_signal' },
                    { label: 'Congress', field: 'congress_signal' },
                    { label: '', field: '_actions' },
                  ].map(col => (
                    <th key={col.field} className="px-2 py-2 text-left">
                      <SortHeader
                        label={col.label}
                        field={col.field}
                        sortField={sortField}
                        sortDir={sortDir}
                        onSort={setSortField}
                      />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sorted.map(stock => {
                  const r = ratings[stock.ticker]
                  return (
                    <tr
                      key={stock.ticker}
                    >
                      <td className="px-2 py-2 font-bold">
                        <div className="flex items-center gap-1.5">
                          <TickerLink ticker={stock.ticker} />
                          {stock.held && (
                            <span
                              className="text-[9px] font-bold px-1 py-0.5 rounded bg-cyan/15 text-cyan-light border border-cyan/30"
                              title={
                                stock.position
                                  ? `You hold ${stock.position.shares} @ avg ${stock.position.avg_cost ?? '-'} · value ${Math.round(stock.position.value).toLocaleString()} ${stock.position.currency} · unrealised ${stock.position.unrealised_pnl >= 0 ? '+' : ''}${Math.round(stock.position.unrealised_pnl).toLocaleString()}`
                                  : 'In your portfolio'
                              }
                            >
                              HELD
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-2 py-2 text-text-primary truncate max-w-[120px]">
                        {stock.name}
                        {stock.position && (
                          <span className={clsx(
                            'ml-1.5 text-[10px] font-mono',
                            stock.position.unrealised_pnl >= 0 ? 'text-emerald-light/70' : 'text-rose-light/70',
                          )}>
                            ({stock.position.unrealised_pnl >= 0 ? '+' : ''}{Math.round(stock.position.unrealised_pnl).toLocaleString()})
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-text-primary font-mono">{usd(stock.price)}</td>
                      <td className="px-2 py-2">
                        <span className={clsx(stock.daily_change_pct >= 0 ? 'delta-chip-pos' : 'delta-chip-neg', 'text-[10px] px-1.5')}>
                          {pct(stock.daily_change_pct)}
                        </span>
                      </td>
                      <td className="px-2 py-2"><QuantBadge rating={r?.quant_rating} /></td>
                      <td className="px-2 py-2"><GradeBadge grade={r?.valuation.grade} /></td>
                      <td className="px-2 py-2"><GradeBadge grade={r?.growth.grade} /></td>
                      <td className="px-2 py-2"><GradeBadge grade={r?.profitability.grade} /></td>
                      <td className="px-2 py-2"><GradeBadge grade={r?.momentum.grade} /></td>
                      <td className="px-2 py-2"><GradeBadge grade={r?.eps_revisions.grade} /></td>
                      <td className="px-2 py-2 text-text-secondary font-mono">{capFmt(stock.market_cap_m)}</td>
                      <td className="px-2 py-2 text-text-secondary font-mono">
                        {stock.pe_ratio != null ? stock.pe_ratio.toFixed(1) : '-'}
                      </td>
                      <td className="px-2 py-2 text-text-secondary font-mono">
                        {stock.pb_ratio != null ? stock.pb_ratio.toFixed(2) : '-'}
                      </td>
                      <td className="px-2 py-2 text-text-secondary font-mono">
                        {stock.ps_ratio != null ? stock.ps_ratio.toFixed(2) : '-'}
                      </td>
                      <td className="px-2 py-2 text-text-secondary font-mono">
                        {stock.dividend_yield != null ? `${stock.dividend_yield.toFixed(2)}%` : '-'}
                      </td>
                      <td className="px-2 py-2 text-text-secondary font-mono">
                        {stock.beta != null ? stock.beta.toFixed(2) : '-'}
                      </td>
                      <td className={clsx(
                        'px-2 py-2 font-mono',
                        stock.rsi == null ? 'text-text-muted'
                          : stock.rsi < 30 ? 'text-rose-light'
                          : stock.rsi > 70 ? 'text-emerald-light'
                          : 'text-text-secondary',
                      )}>
                        {stock.rsi != null ? stock.rsi.toFixed(1) : '-'}
                      </td>
                      <td className="px-2 py-2"><SignalBadge signal={stock.insider_signal} /></td>
                      <td className="px-2 py-2"><SignalBadge signal={stock.congress_signal} /></td>
                      <td className="px-2 py-2">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/app/chart?ticker=${stock.ticker}`) }}
                            className="p-1 rounded hover:bg-blue/20 text-text-muted hover:text-blue-light transition-colors"
                            title="View chart"
                          >
                            <CandlestickChart size={12} />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); navigateToTerminalCommand(navigate, `/quote ${stock.ticker}`) }}
                            className="p-1 rounded hover:bg-emerald/20 text-text-muted hover:text-emerald-light transition-colors"
                            title="View in Terminal"
                          >
                            <Monitor size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
